import React, { useEffect, useState } from "react";
import { Calendar, Save, Search, Soup, Loader2, FileDown, Copy, Tag } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, NUM, formatApiError, trWeekday, trDate } from "@/lib/api";
import { Card } from "@/components/Cards";
import DayMenuHint from "@/components/DayMenuHint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const MEALS = ["Kahvaltı", "Öğle Yemeği", "Akşam Yemeği", "Gece Vardiyası"];
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function DailyEntry() {
  const [date, setDate] = useState(todayISO());
  const [meal, setMeal] = useState("Öğle Yemeği");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [onlyEmpty, setOnlyEmpty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [prefilledFrom, setPrefilledFrom] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/daily-entries", { params: { date, meal_type: meal } });
      setRows(res.data);
      setPrefilledFrom(res.headers["x-prefilled-from"] || "");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, meal]);

  const copyPrevious = async () => {
    try {
      const { data } = await api.get("/daily-entries/previous", { params: { date, meal_type: meal } });
      setRows((rs) => rs.map((x) => {
        const n = data.counts[x.customer_id] ?? 0;
        return { ...x, headcount: n, total: n * x.unit_price * (1 + (x.vat_rate || 0) / 100), prefilled: true };
      }));
      setPrefilledFrom(data.from_date);
      toast.success(`${trDate(data.from_date)} sayıları getirildi — kaydetmeyi unutmayın`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setHead = (id, val) => {
    const n = Math.max(0, parseInt(val || 0, 10) || 0);
    setRows((r) => r.map((x) => (x.customer_id === id ? { ...x, headcount: n, total: n * x.unit_price * (1 + (x.vat_rate || 0) / 100) } : x)));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const entries = rows.map((r) => ({ customer_id: r.customer_id, headcount: r.headcount }));
      const { data } = await api.post("/daily-entries/save", { date, meal_type: meal, entries });
      toast.success(data.message + " ve cari hesaplara işlendi");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filtered = rows.filter(
    (r) => r.name.toLowerCase().includes(search.toLowerCase()) && (!onlyEmpty || r.headcount === 0)
  );
  const totalRev = rows.reduce((s, r) => s + r.total, 0);
  const totalHead = rows.reduce((s, r) => s + r.headcount, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Günlük Kişi Sayısı Girişi</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Tarih ve öğün seçin, kişi sayılarını girin — ciro anında hesaplanır (KDV dahil).
          </p>
        </div>
        <div className="flex gap-2">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/daily-entries/labels-pdf?date=${date}&meal_type=${encodeURIComponent(meal)}`} target="_blank" rel="noreferrer" data-testid="daily-entry-labels-link" title="OPN-1065 (65'li) etiket şablonu — sayısı 0 olanlar hariç">
            <Button variant="outline" className="rounded-full h-11 px-5 font-semibold gap-2"><Tag className="h-4 w-4" /> Etiket PDF</Button>
          </a>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/daily-entries/pdf?date=${date}`} target="_blank" rel="noreferrer" data-testid="daily-entry-pdf-link">
            <Button variant="outline" className="rounded-full h-11 px-5 font-semibold gap-2"><FileDown className="h-4 w-4" /> Günün PDF'i</Button>
          </a>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/daily-entries/monthly-pdf?month=${date.slice(0, 7)}`} target="_blank" rel="noreferrer" data-testid="daily-entry-monthly-pdf-link" title="Seçili ay için tüm öğünler (kahvaltı, öğle, akşam, gece) tek tablo">
            <Button variant="outline" className="rounded-full h-11 px-5 font-semibold gap-2"><Calendar className="h-4 w-4" /> Ayın PDF'i</Button>
          </a>
          <Button data-testid="daily-entry-save-all-btn" onClick={saveAll} disabled={saving}
            className="rounded-full h-11 px-6 font-semibold gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Tümünü Kaydet
        </Button>
        </div>
      </div>

      <DayMenuHint date={date} meal={meal} />

      <Card className="p-5">
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input type="date" data-testid="daily-entry-date-input" value={date}
              onChange={(e) => setDate(e.target.value)} className="h-10 pl-9 w-[180px] font-mono" />
          </div>
          <span className="text-sm text-muted-foreground font-medium" data-testid="daily-entry-weekday">{trWeekday(date)}</span>
          <Select value={meal} onValueChange={setMeal}>
            <SelectTrigger className="h-10 w-[190px]" data-testid="daily-entry-meal-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MEALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Müşteri ara…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="h-10 pl-9" data-testid="daily-entry-search" />
          </div>
          <Button variant={onlyEmpty ? "default" : "outline"} onClick={() => setOnlyEmpty(!onlyEmpty)}
            className="h-10 rounded-lg" data-testid="daily-entry-empty-filter">
            Sayı Girilmeyenler
          </Button>
          <Button variant="outline" onClick={copyPrevious} className="h-10 rounded-lg gap-1.5" data-testid="daily-entry-copy-prev">
            <Copy className="h-4 w-4" /> Dünü Kopyala
          </Button>
        </div>
      </Card>

      {!loading && prefilledFrom && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-300" data-testid="daily-prefilled-banner">
          {trDate(prefilledFrom)} günün sayıları otomatik getirildi — kontrol edip <b>Tümünü Kaydet</b>'e basın.
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left">
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Müşteri</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-right">Birim Fiyat (KDV dahil)</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center w-40">Kişi Sayısı</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-right">Toplam Tutar (KDV dahil)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-5 py-12 text-center text-muted-foreground">Yükleniyor…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={4} className="px-5 py-12 text-center text-muted-foreground" data-testid="daily-empty">Bu öğün için fiyatı tanımlı müşteri yok. Müşteri kartından {meal} fiyatı girin.</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.customer_id} data-testid={`daily-entry-customer-row-${r.customer_id}`}
                  className="border-b border-border/60 hover:bg-accent/40 transition-colors">
                  <td className="px-5 py-3 font-medium">
                    {r.name}
                    {r.saved && <span className="ml-2 text-[10px] uppercase font-semibold text-emerald-400">kayıtlı</span>}
                    {r.prefilled && <span className="ml-2 text-[10px] uppercase font-semibold text-amber-400" data-testid={`daily-prefilled-${r.customer_id}`}>önceki gün</span>}
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-muted-foreground">{TRY(r.unit_price * (1 + (r.vat_rate || 0) / 100))}{r.vat_rate > 0 && <span className="ml-1 text-[10px] text-primary font-semibold" data-testid={`daily-vat-${r.customer_id}`}>%{r.vat_rate} KDV dahil</span>}</td>
                  <td className="px-5 py-3">
                    <Input type="number" min={0} inputMode="numeric" value={r.headcount || ""} placeholder="0"
                      onChange={(e) => setHead(r.customer_id, e.target.value)}
                      onFocus={(e) => e.target.select()}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter" && e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
                        e.preventDefault();
                        const inputs = Array.from(document.querySelectorAll('[data-testid^="daily-entry-input-"]'));
                        const i = inputs.indexOf(e.target);
                        const next = inputs[e.key === "ArrowUp" ? i - 1 : i + 1];
                        if (next) next.focus();
                        else if (e.key === "Enter") document.querySelector('[data-testid="daily-entry-save-all-btn"]')?.focus();
                      }}
                      className="h-9 text-center font-mono mx-auto w-28 no-spin"
                      data-testid={`daily-entry-input-${r.customer_id}`} />
                  </td>
                  <td className="px-5 py-3 text-right font-mono font-semibold">{TRY(r.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-primary/5 border-t-2 border-primary/30 font-semibold">
                <td className="px-5 py-3">TOPLAM</td>
                <td />
                <td className="px-5 py-3 text-center font-mono flex items-center justify-center gap-1.5">
                  <Soup className="h-4 w-4 text-sky-400" /> {NUM(totalHead)} kişi
                </td>
                <td className="px-5 py-3 text-right font-mono text-primary text-base">{TRY(totalRev)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
    </div>
  );
}
